import React, { useState, useEffect, useRef, useCallback } from "react"
import { createPortal } from "react-dom"
import { Link, useNavigate } from "react-router-dom"
import { Users, Briefcase, Clock, MagnifyingGlass } from "@phosphor-icons/react"
import { useGlobalSearch } from "../../hooks"
import { useUIStore } from "../../stores"
import { cn } from "../../lib/utils"

const RECENT_SEARCHES_KEY = "intuidesk_recent_searches"
const MAX_RECENT_SEARCHES = 5

interface RecentSearch {
  query: string
  type: "contact" | "matter"
  id: string
  name: string
  timestamp: number
}

function getRecentSearches(): RecentSearch[] {
  try {
    const stored = localStorage.getItem(RECENT_SEARCHES_KEY)
    return stored ? JSON.parse(stored) : []
  } catch {
    return []
  }
}

function addRecentSearch(search: Omit<RecentSearch, "timestamp">) {
  const recent = getRecentSearches()
  const filtered = recent.filter(
    (r) => !(r.type === search.type && r.id === search.id)
  )
  const updated = [{ ...search, timestamp: Date.now() }, ...filtered].slice(
    0,
    MAX_RECENT_SEARCHES
  )
  localStorage.setItem(RECENT_SEARCHES_KEY, JSON.stringify(updated))
}

function clearRecentSearches() {
  localStorage.removeItem(RECENT_SEARCHES_KEY)
}

function formatTimeAgo(timestamp: number): string {
  const diffMs = Date.now() - timestamp
  const diffMins = Math.floor(diffMs / 60000)
  const diffHours = Math.floor(diffMs / 3600000)
  const diffDays = Math.floor(diffMs / 86400000)

  if (diffMins < 60) return `${diffMins}m ago`
  if (diffHours < 24) return `${diffHours}h ago`
  return `${diffDays}d ago`
}

// Skeleton shimmer component
function Skeleton({ className }: { className?: string }) {
  return (
    <div
      className={cn(
        "animate-pulse bg-gradient-to-r from-muted via-muted/50 to-muted bg-[length:200%_100%]",
        "rounded",
        className
      )}
    />
  )
}

// Skeleton row for loading state
function SkeletonRow() {
  return (
    <div className="px-3 py-2">
      <Skeleton className="h-4 w-3/4 mb-1.5" />
      <Skeleton className="h-3 w-1/2" />
    </div>
  )
}

export function SearchResults() {
  const navigate = useNavigate()
  const {
    globalSearchQuery,
    searchResultsOpen,
    setSearchResultsOpen,
    setGlobalSearchQuery,
  } = useUIStore()
  const { data, isLoading } = useGlobalSearch(globalSearchQuery)
  const [recentSearches, setRecentSearches] = useState<RecentSearch[]>([])
  const [position, setPosition] = useState({ top: 0, left: 0, width: 0 })
  const [selectedIndex, setSelectedIndex] = useState(-1)
  const containerRef = useRef<HTMLDivElement>(null)
  const itemRefs = useRef<(HTMLAnchorElement | HTMLButtonElement | null)[]>([])

  // Build flat list of all navigable items
  const allItems = React.useMemo(() => {
    if (!data) return []
    const items: Array<{
      type: "contact" | "matter"
      id: string
      name: string
      data: any
    }> = []
    data.contacts.forEach((c) =>
      items.push({ type: "contact", id: c._id, name: c.display_name, data: c })
    )
    data.matters.forEach((m) =>
      items.push({ type: "matter", id: m.id, name: m.name, data: m })
    )
    return items
  }, [data])

  // Load recent searches on mount
  useEffect(() => {
    setRecentSearches(getRecentSearches())
  }, [searchResultsOpen])

  // Reset selection when results change
  useEffect(() => {
    setSelectedIndex(-1)
  }, [data, globalSearchQuery])

  // Calculate position based on search input
  useEffect(() => {
    if (searchResultsOpen) {
      const searchContainer = document.querySelector(
        "[data-search-container]"
      ) as HTMLElement
      if (searchContainer) {
        const rect = searchContainer.getBoundingClientRect()
        setPosition({
          top: rect.bottom + 8,
          left: rect.left,
          width: Math.max(rect.width, 400),
        })
      }
    }
  }, [searchResultsOpen])

  // Scroll selected item into view
  useEffect(() => {
    if (selectedIndex >= 0 && itemRefs.current[selectedIndex]) {
      itemRefs.current[selectedIndex]?.scrollIntoView({
        block: "nearest",
        behavior: "smooth",
      })
    }
  }, [selectedIndex])

  // Keyboard navigation
  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (!searchResultsOpen || !data) return

      const contactsCount = data.contacts.length
      const mattersCount = data.matters.length

      // Determine current column based on selectedIndex
      const isInContacts = selectedIndex < contactsCount
      const isInMatters = selectedIndex >= contactsCount

      switch (e.key) {
        case "ArrowDown":
          e.preventDefault()
          if (selectedIndex === -1) {
            // Nothing selected, select first contact (or first matter if no contacts)
            setSelectedIndex(contactsCount > 0 ? 0 : contactsCount)
          } else if (isInContacts) {
            // Stay within contacts column
            const nextInContacts = selectedIndex + 1
            if (nextInContacts < contactsCount) {
              setSelectedIndex(nextInContacts)
            }
          } else if (isInMatters) {
            // Stay within matters column
            const nextInMatters = selectedIndex + 1
            if (nextInMatters < contactsCount + mattersCount) {
              setSelectedIndex(nextInMatters)
            }
          }
          break
        case "ArrowUp":
          e.preventDefault()
          if (selectedIndex === -1) {
            // Nothing selected, select first contact (or first matter if no contacts)
            setSelectedIndex(contactsCount > 0 ? 0 : contactsCount)
          } else if (isInContacts) {
            // Stay within contacts column
            if (selectedIndex > 0) {
              setSelectedIndex(selectedIndex - 1)
            }
          } else if (isInMatters) {
            // Stay within matters column
            if (selectedIndex > contactsCount) {
              setSelectedIndex(selectedIndex - 1)
            }
          }
          break
        case "ArrowRight":
          e.preventDefault()
          // Jump to top of matters column
          if (mattersCount > 0) {
            setSelectedIndex(contactsCount)
          }
          break
        case "ArrowLeft":
          e.preventDefault()
          // Jump to top of contacts column
          if (contactsCount > 0) {
            setSelectedIndex(0)
          }
          break
        case "Enter":
          e.preventDefault()
          if (selectedIndex >= 0 && allItems[selectedIndex]) {
            const item = allItems[selectedIndex]
            addRecentSearch({ query: globalSearchQuery, type: item.type, id: item.id, name: item.name })
            setSearchResultsOpen(false)
            setGlobalSearchQuery("")
            navigate(`/${item.type}s/${item.id}`)
          }
          break
        case "Escape":
          e.preventDefault()
          setSearchResultsOpen(false)
          break
      }
    },
    [searchResultsOpen, data, allItems, selectedIndex, navigate, setSearchResultsOpen, globalSearchQuery, setGlobalSearchQuery]
  )

  useEffect(() => {
    document.addEventListener("keydown", handleKeyDown)
    return () => document.removeEventListener("keydown", handleKeyDown)
  }, [handleKeyDown])

  if (!searchResultsOpen) return null

  const hasQuery = globalSearchQuery.length >= 2
  const hasResults =
    data && (data.contacts.length > 0 || data.matters.length > 0)

  const handleResultClick = (
    type: "contact" | "matter",
    id: string,
    name: string
  ) => {
    addRecentSearch({ query: globalSearchQuery, type, id, name })
    setSearchResultsOpen(false)
    setGlobalSearchQuery("")
  }

  const handleRecentClick = (search: RecentSearch) => {
    setSearchResultsOpen(false)
    setGlobalSearchQuery("")
    navigate(`/${search.type}s/${search.id}`)
  }

  const handleClearRecent = () => {
    clearRecentSearches()
    setRecentSearches([])
  }

  const handleBackdropClick = () => {
    setSearchResultsOpen(false)
  }

  // Get index offset for matters (contacts come first)
  const getItemIndex = (type: "contact" | "matter", idx: number) => {
    if (type === "contact") return idx
    return (data?.contacts.length || 0) + idx
  }

  return createPortal(
    <>
      {/* Backdrop with subtle blur - slightly stronger in light mode */}
      <div
        className="overlay-backdrop fixed inset-0 z-[99] bg-black/5 dark:bg-black/10 backdrop-blur-sm"
        onClick={handleBackdropClick}
      />

      {/* Glassmorphism container */}
      <div
        ref={containerRef}
        style={{
          top: position.top,
          left: position.left,
          width: position.width,
        }}
        className={cn(
          "fixed overflow-hidden",
          "max-w-[560px]",
          "rounded-2xl",
          // Glassmorphism - theme aware
          "overlay-backdrop search-results-panel bg-background/80 dark:bg-background/70 backdrop-blur-md backdrop-saturate-150",
          "border border-black/10 dark:border-white/10",
          "shadow-[0_8px_32px_rgba(0,0,0,0.08),0_0_0_1px_rgba(0,0,0,0.05)] dark:shadow-[0_8px_32px_rgba(0,0,0,0.12),0_0_0_1px_rgba(255,255,255,0.05)_inset]",
          "z-[100]",
          // Animation
          "animate-in fade-in slide-in-from-top-2 duration-150"
        )}
      >
        {isLoading ? (
          // Skeleton loading state
          <div className="grid grid-cols-2">
            <div className="py-2 border-r border-black/5 dark:border-white/5">
              <div className="px-3 py-1.5 mb-1">
                <Skeleton className="h-3 w-20" />
              </div>
              <SkeletonRow />
              <SkeletonRow />
              <SkeletonRow />
            </div>
            <div className="py-2">
              <div className="px-3 py-1.5 mb-1">
                <Skeleton className="h-3 w-16" />
              </div>
              <SkeletonRow />
              <SkeletonRow />
              <SkeletonRow />
            </div>
          </div>
        ) : !hasQuery ? (
          // No query - show recent or empty
          <div>
            {recentSearches.length > 0 ? (
              <>
                <div className="flex items-center justify-between px-3 py-2 bg-black/[0.03] dark:bg-white/5 backdrop-blur-sm border-b border-black/5 dark:border-white/5">
                  <div className="flex items-center gap-1.5">
                    <Clock className="h-3 w-3 text-muted-foreground/70" />
                    <span className="text-[10px] font-medium text-muted-foreground/70 uppercase tracking-widest">
                      Recent
                    </span>
                  </div>
                  <button
                    onClick={handleClearRecent}
                    className="text-[10px] text-muted-foreground/50 hover:text-muted-foreground transition-colors"
                  >
                    Clear
                  </button>
                </div>
                <div className="py-1">
                  {recentSearches.map((search, index) => (
                    <button
                      key={`${search.type}-${search.id}-${index}`}
                      onClick={() => handleRecentClick(search)}
                      className={cn(
                        "flex items-center gap-2 w-full px-3 py-2 text-left",
                        "transition-all duration-150",
                        "hover:bg-black/[0.03] dark:hover:bg-white/5 hover:border-l-2 hover:border-l-primary/50 hover:pl-[10px]"
                      )}
                    >
                      <div className="flex-1 min-w-0">
                        <p className="text-[13px] font-medium text-foreground truncate">
                          {search.name}
                        </p>
                        <p className="text-[11px] text-muted-foreground/70">
                          {search.type === "contact" ? "Contact" : "Matter"} ·{" "}
                          {formatTimeAgo(search.timestamp)}
                        </p>
                      </div>
                    </button>
                  ))}
                </div>
              </>
            ) : (
              <div className="py-12 text-center">
                <MagnifyingGlass className="h-5 w-5 text-muted-foreground/30 mx-auto mb-2" />
                <p className="text-[13px] text-muted-foreground/70">
                  Type to search matters & contacts
                </p>
              </div>
            )}
          </div>
        ) : !hasResults ? (
          // No results
          <div className="py-12 text-center">
            <MagnifyingGlass className="h-5 w-5 text-muted-foreground/30 mx-auto mb-2" />
            <p className="text-[13px] text-muted-foreground/70">
              No results for "{globalSearchQuery}"
            </p>
          </div>
        ) : (
          // Results grid
          <div className="grid grid-cols-2 max-h-[380px] overflow-y-auto">
            {/* Contacts column */}
            <div className="border-r border-black/5 dark:border-white/5">
              {/* Frosted header */}
              <div className="sticky top-0 flex items-center gap-1.5 px-3 py-2 bg-black/[0.03] dark:bg-white/5 backdrop-blur-sm border-b border-black/5 dark:border-white/5">
                <Users className="h-3 w-3 text-muted-foreground/70" />
                <span className="text-[10px] font-medium text-muted-foreground/70 uppercase tracking-widest">
                  Contacts
                </span>
                <span className="ml-auto text-[10px] text-muted-foreground/50 bg-black/[0.06] dark:bg-white/10 px-1.5 py-0.5 rounded-full">
                  {data.contacts.length}
                </span>
              </div>

              {data.contacts.length === 0 ? (
                <p className="text-[11px] text-muted-foreground/50 px-3 py-4 text-center">
                  No contacts
                </p>
              ) : (
                <div className="py-1">
                  {data.contacts.map((contact, idx) => {
                    const itemIndex = getItemIndex("contact", idx)
                    const isSelected = selectedIndex === itemIndex
                    return (
                      <Link
                        key={contact._id}
                        ref={(el) => { itemRefs.current[itemIndex] = el }}
                        to={`/contacts/${contact._id}`}
                        onClick={() =>
                          handleResultClick(
                            "contact",
                            contact._id,
                            contact.display_name
                          )
                        }
                        onMouseEnter={() => setSelectedIndex(itemIndex)}
                        className={cn(
                          "block px-3 py-2 transition-all duration-150",
                          isSelected
                            ? "bg-black/[0.06] dark:bg-white/10 border-l-2 border-l-primary pl-[10px]"
                            : "hover:bg-black/[0.03] dark:hover:bg-white/5 border-l-2 border-l-transparent"
                        )}
                      >
                        <p className="text-[13px] font-medium text-foreground truncate">
                          {contact.display_name}
                        </p>
                        <p className="text-[11px] text-muted-foreground/70 truncate">
                          {[contact.email, contact.phone]
                            .filter(Boolean)
                            .join(" · ") || "No contact info"}
                        </p>
                      </Link>
                    )
                  })}
                </div>
              )}
            </div>

            {/* Matters column */}
            <div>
              {/* Frosted header */}
              <div className="sticky top-0 flex items-center gap-1.5 px-3 py-2 bg-black/[0.03] dark:bg-white/5 backdrop-blur-sm border-b border-black/5 dark:border-white/5">
                <Briefcase className="h-3 w-3 text-muted-foreground/70" />
                <span className="text-[10px] font-medium text-muted-foreground/70 uppercase tracking-widest">
                  Matters
                </span>
                <span className="ml-auto text-[10px] text-muted-foreground/50 bg-black/[0.06] dark:bg-white/10 px-1.5 py-0.5 rounded-full">
                  {data.matters.length}
                </span>
              </div>

              {data.matters.length === 0 ? (
                <p className="text-[11px] text-muted-foreground/50 px-3 py-4 text-center">
                  No matters
                </p>
              ) : (
                <div className="py-1">
                  {data.matters.map((matter, idx) => {
                    const itemIndex = getItemIndex("matter", idx)
                    const isSelected = selectedIndex === itemIndex
                    return (
                      <Link
                        key={matter._id}
                        ref={(el) => { itemRefs.current[itemIndex] = el }}
                        to={`/matters/${matter.id}`}
                        onClick={() =>
                          handleResultClick("matter", matter.id, matter.name)
                        }
                        onMouseEnter={() => setSelectedIndex(itemIndex)}
                        className={cn(
                          "block px-3 py-2 transition-all duration-150",
                          isSelected
                            ? "bg-black/[0.06] dark:bg-white/10 border-l-2 border-l-primary pl-[10px]"
                            : "hover:bg-black/[0.03] dark:hover:bg-white/5 border-l-2 border-l-transparent"
                        )}
                      >
                        <p className="text-[13px] font-medium text-foreground truncate">
                          {matter.name}
                        </p>
                        <p className="text-[11px] text-muted-foreground/70 truncate">
                          <span className="inline-block w-1.5 h-1.5 rounded-full bg-primary/60 mr-1.5 align-middle" />
                          {matter.id}{matter.identifier && matter.identifier !== matter.id ? ` / ${matter.identifier}` : ""}
                          {matter.current_step_name && (
                            <span className="ml-1.5 text-muted-foreground/50">
                              · {matter.current_step_name}
                            </span>
                          )}
                        </p>
                      </Link>
                    )
                  })}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Minimal footer with keyboard hints */}
        <div className="flex items-center justify-center gap-3 px-3 py-2 border-t border-black/5 dark:border-white/5 bg-black/[0.02] dark:bg-white/[0.02]">
          <span className="text-[10px] text-muted-foreground/50">
            <kbd className="px-1 py-0.5 bg-black/[0.04] dark:bg-white/5 rounded text-[9px] font-medium mr-1">
              ↑↓
            </kbd>
            Navigate
          </span>
          <span className="text-[10px] text-muted-foreground/50">
            <kbd className="px-1 py-0.5 bg-black/[0.04] dark:bg-white/5 rounded text-[9px] font-medium mr-1">
              ←→
            </kbd>
            Switch
          </span>
          <span className="text-[10px] text-muted-foreground/50">
            <kbd className="px-1 py-0.5 bg-black/[0.04] dark:bg-white/5 rounded text-[9px] font-medium mr-1">
              ↵
            </kbd>
            Select
          </span>
          <span className="text-[10px] text-muted-foreground/50">
            <kbd className="px-1 py-0.5 bg-black/[0.04] dark:bg-white/5 rounded text-[9px] font-medium mr-1">
              Esc
            </kbd>
            Close
          </span>
        </div>
      </div>
    </>,
    document.body
  )
}
